import { useEffect, useRef, useState } from 'react';

import { autocompleteClubsApi } from '@/lib/api/clubsApi';
import type { ClubAutocompleteItem } from '@api-client/clubs';
import { Building2, ChevronDown, Loader2, X } from 'lucide-react';

interface ClubAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (club: ClubAutocompleteItem) => void;
  onClear: () => void;
  placeholder?: string;
  className?: string;
}

export function ClubAutocomplete({
  value,
  onChange,
  onSelect,
  onClear,
  placeholder = 'Rechercher un club existant…',
  className = '',
}: ClubAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<ClubAutocompleteItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const search = (q: string) => {
    clearTimeout(timerRef.current);
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    timerRef.current = setTimeout(() => {
      setIsLoading(true);
      autocompleteClubsApi(q.trim())
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setIsLoading(false));
    }, 300);
  };

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const handleInput = (q: string) => {
    onChange(q);
    search(q);
    setOpen(true);
  };

  const handleSelect = (club: ClubAutocompleteItem) => {
    onChange(club.name);
    onSelect(club);
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setResults([]);
    setOpen(false);
    onClear();
  };

  return (
    <div className={`relative ${className}`}>
      <div className='relative'>
        <Building2 className='absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none' />
        <input
          type='text'
          value={value}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className='w-full pl-11 pr-16 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors'
          autoComplete='off'
        />
        <div className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1'>
          {isLoading ? (
            <Loader2 className='w-4 h-4 text-slate-400 animate-spin' />
          ) : value ? (
            <button type='button' onClick={handleClear} className='text-slate-400 hover:text-slate-600'>
              <X className='w-4 h-4' />
            </button>
          ) : (
            <ChevronDown className='w-4 h-4 text-slate-400' />
          )}
        </div>
      </div>

      {open && results.length > 0 && (
        <ul className='absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto'>
          {results.map(club => (
            <li key={club.id}>
              <button
                type='button'
                onMouseDown={() => handleSelect(club)}
                className='w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors'
              >
                <div className='flex items-center gap-2'>
                  <Building2 className='w-3.5 h-3.5 text-slate-400 shrink-0' />
                  <span className='text-sm text-slate-700'>{club.name}</span>
                </div>
                <span className='text-xs text-slate-400 shrink-0'>
                  {club.city} ({club.department})
                  {club.hasAdmin && <span className='ml-1.5 text-emerald-600 font-semibold'>Admin actif</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
